/**
 * Authentication Context — user accounts (JWT) with legacy shared-password
 * fallback kept alive during the migration.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { can as canCheck, isSuperAdmin as superAdminCheck } from '../utils/permissions';

const AuthContext = createContext();

export const TOKEN_KEY = 'keycrew-auth-token';
export const USER_KEY = 'keycrew-auth-user';
const LEGACY_TOKEN = 'keycrew-access-granted';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null); // null for legacy sessions
  // Explicit legacy marker, keyed off the token — never inferred from a
  // missing profile, so a JWT session with a failed profile load fails closed.
  const [isLegacy, setIsLegacy] = useState(false);
  const [securityNotice, setSecurityNotice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return;
    }
    if (token === LEGACY_TOKEN) {
      setIsLegacy(true);
      setIsAuthenticated(true);
      setLoading(false);
      // Still ask for the security notice. A legacy session needs no profile — it acts as super
      // admin by definition — but it is precisely the session the enforcement warning is about,
      // and skipping this fetch meant the person most affected was the only one never told.
      // Deliberately does not set `user`: legacy sessions have no profile and other code
      // (isLegacySession, the permission bypass) depends on that staying null.
      fetch(`${backendUrl}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => setSecurityNotice(data?.data?.security_notice || null))
        .catch(() => { /* the banner is advisory; never block a session on it */ });
      return;
    }
    // JWT session — restore cached profile immediately, then revalidate.
    // A temp-password session is NOT authenticated: the login form must
    // stay mounted so its change-password step can render.
    const cached = localStorage.getItem(USER_KEY);
    if (cached) {
      try {
        const cachedUser = JSON.parse(cached);
        setUser(cachedUser);
        setIsAuthenticated(!cachedUser.must_change_password);
      } catch { /* ignore bad cache */ }
    }
    fetch(`${backendUrl}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((data) => {
        const freshUser = data?.data?.user;
        if (freshUser) {
          setUser(freshUser);
          localStorage.setItem(USER_KEY, JSON.stringify(freshUser));
          setIsAuthenticated(!freshUser.must_change_password);
        }
        // Super-admin-only: whether the permission rules are actually enforced. Not persisted —
        // it describes the server's current configuration, so a stale cached copy would be worse
        // than none.
        setSecurityNotice(data?.data?.security_notice || null);
      })
      .catch((status) => {
        if (status === 401) {
          // Token expired / revoked (deactivation, force-logout)
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
          setUser(null);
          setIsAuthenticated(false);
        }
        // Network errors keep the cached session — backend enforces anyway
      })
      .finally(() => setLoading(false));
  }, []);

  /**
   * login({ email, password }) → user-account login
   * login({ password })        → legacy shared-password login
   */
  const login = async ({ email, password }) => {
    try {
      const response = await fetch(`${backendUrl}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(email ? { email, password } : { password }),
      });
      const data = await response.json();

      if (data.success && data.data?.authenticated) {
        localStorage.setItem(TOKEN_KEY, data.data.token);
        setIsLegacy(data.data.token === LEGACY_TOKEN);
        if (data.data.user) {
          localStorage.setItem(USER_KEY, JSON.stringify(data.data.user));
          setUser(data.data.user);
        } else {
          localStorage.removeItem(USER_KEY);
          setUser(null);
        }
        const mustChange = !!data.data.user?.must_change_password;
        // Keep the login form mounted until the temp password is replaced
        setIsAuthenticated(!mustChange);
        return { success: true, mustChangePassword: mustChange };
      }
      return {
        success: false,
        error: data?.error?.message || data.message || 'Invalid credentials',
      };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Connection error' };
    }
  };

  const logout = useCallback(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      // Fire-and-forget so the audit log records the logout
      fetch(`${backendUrl}/api/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
    setIsLegacy(false);
    setIsAuthenticated(false);
  }, []);

  // Re-read the profile when the tab regains focus. A super admin can change what a role may do
  // at any moment; without this an open tab keeps yesterday's permissions array until reload, and
  // the UI shows buttons the server no longer backs. Deliberately NOT solved by bumping
  // session_version, which would log everyone out every time a checkbox moved.
  useEffect(() => {
    const onFocus = () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token || token === LEGACY_TOKEN) return;
      fetch(`${backendUrl}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          const fresh = data?.data?.user;
          if (fresh) {
            setUser(fresh);
            localStorage.setItem(USER_KEY, JSON.stringify(fresh));
          }
          if (data?.data) setSecurityNotice(data.data.security_notice || null);
        })
        .catch(() => { /* offline: keep what we have */ });
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [backendUrl]);

  const value = {
    isAuthenticated,
    loading,
    user,                                   // null for legacy sessions
    isLegacySession: isLegacy,
    // Legacy sessions act as super admin (matching the backend's legacy
    // identity); everything else derives from the server-sent permissions.
    can: (permission) => isLegacy || canCheck(user, permission),
    isSuperAdmin: isLegacy || superAdminCheck(user),
    securityNotice,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
